/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	type ComponentRef,
	Injectable,
	Injector,
	type ViewContainerRef,
	inject
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { v4 } from "uuid";
import {
	type NotificationAction
} from "./notification/notification.component";
import { ToastComponent } from "./toast/toast.component";

export interface ToastAction {
	label: string;
	callback: () => void;
	color: "brand" | "gray" | "white" | "error" | "warning" | "success" | "info";
	hierarchy?: "primary" | "secondary" | "tertiary";
}

export interface ToastMessage {
	key?: string;
	title: string;
	icon?: string;
	message: string;
	duration?: number;
	image?: string;
	imageStyle?: "thumbnail" | "preview";
	actions?: NotificationAction[];
	color?: "brand" | "success" | "warning" | "error" | "info" | "gray";
	hierarchy?: "secondary" | "tertiary";
	translate?: boolean;
}

@Injectable({
	providedIn: "root",
})
export class ToastService {
	private readonly injector: Injector = inject(Injector);
	private readonly translate: TranslateService = inject(TranslateService);

	private openedToasts: ComponentRef<ToastComponent>[] = [];

	private vcr?: ViewContainerRef;

	public setViewContainerRef(vcr: ViewContainerRef) {
		this.vcr = vcr;
	}

	public showMessage(message: ToastMessage): ComponentRef<ToastComponent> {
		if (!this.vcr) throw new Error("ViewContainerRef not set");

		const injector = Injector.create({
			providers: [],
			parent: this.injector,
		});

		this.closeAll();

		const actions = [];
		for (const action of message.actions ?? []) {
			actions.push({
				...action,
				label: message.translate
					? this.translate.instant(action.label)
					: action.label,
			});
		}

		const info = {
			key: message.key || v4(),
			color: message.color || "brand",
			hierarchy: message.hierarchy || "secondary",
			icon: message.icon || "",
			title: message.translate
				? this.translate.instant(message.title)
				: message.title,
			message: message.translate
				? this.translate.instant(message.message)
				: message.message,
			actions: actions,
			image: message.image || "",
			imageStyle: message.imageStyle || "thumbnail",
		};

		const toastComponentRef = this.vcr.createComponent(ToastComponent, {
			injector: injector,
		});

		toastComponentRef.setInput("notification", info);

		toastComponentRef.instance.onClose.subscribe(() => {
			this.destroyToast(toastComponentRef);
		});

		this.openedToasts.push(toastComponentRef);

		setTimeout(() => {
			toastComponentRef.instance.close();
		}, message.duration || 5000);

		return toastComponentRef;
	}

	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public showHttpError(error: any): ComponentRef<ToastComponent> {
		const message =
			error.message ??
			error.error?.message ??
			error.error ??
			JSON.stringify(error) ??
			"";

		return this.showError(message);
	}

	public showError(
		error: string,
		duration = 10000,
	): ComponentRef<ToastComponent> {
		return this.showMessage({
			title: this.translate.instant("COMMON.ERROR_TITLE"),
			message: this.translate.instant(error),
			icon: "cb-base-outline-alert-circle",
			color: "error",
			duration: duration,
			hierarchy: "secondary",
		});
	}

	/**
	 * Mostra un toast di successo
	 * @param message Messaggio di successo
	 * @param duration Durata del toast in ms (default 5000)
	 * @returns Riferimento al componente toast creato
	 */
	public showSuccess(
		message: string,
		duration = 5000,
	): ComponentRef<ToastComponent> {
		return this.showMessage({
			title: this.translate.instant("COMMON.SUCCESS"),
			message: this.translate.instant(message),
			icon: "cb-base-outline-checkmark-circle",
			color: "success",
			duration: duration,
			hierarchy: "secondary",
		});
	}

	public closeAll(): void {
		for (const toast of this.openedToasts) {
			this.closeByRef(toast);
		}
	}

	public closeByKey(key: string): void {
		for (const toast of this.openedToasts) {
			if (toast.instance.notification()?.key === key) {
				this.closeByRef(toast);
			}
		}
	}

	public closeByRef(toast: ComponentRef<ToastComponent>): void {
		toast.instance.close();
	}

	private destroyToast(toast: ComponentRef<ToastComponent>): void {
		toast.destroy();
		const index = this.openedToasts.indexOf(toast);
		if (index !== -1) {
			this.openedToasts.splice(index, 1);
		}
	}
}
